import { Component, computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { ModalFactoryInstances } from '@/components/App/ModalFactory/utils/types'
import { ModalClass, ModalEntity, ModalEvent, ModalState } from '@types'

import { MODALS_START_ZINDEX } from './utils/const'
import { OVERLAY_DURATION } from '@/const/common'

import { useNotifications } from '@/plugins/notification'

import { Entity } from '@/entities/utils/common'

export const useModalsStore = defineStore('modals', () => {
  // INIT

  const { warn } = useNotifications()

  // STORE

  const list = ref<ModalFactoryInstances>(new Map())
  const zIndex = ref(MODALS_START_ZINDEX)

  // ACTIONS

  const init = (key: string, object: Component | Entity<any>, { allowTray = false, removeOnClose = true }: { allowTray?: boolean, removeOnClose?: boolean } = {}) => {
    if (list.value.has(key)) return list.value.get(key)

    if (!(object instanceof Entity)) {
      const instance = new ModalEntity(object)
      const modalInstance = new ModalClass(instance, allowTray)
      list.value.set(key, modalInstance)
      removeOnClose && modalInstance.addEventListener(ModalEvent.CLOSE, () => {
        remove(key)
      })
      return modalInstance
    }

    const modalInstance = new ModalClass(object, allowTray)
    modalInstance.conditionalClose = async function (force = false) {
      if (!object.isChanged && !object.isNew) {
        if (force) {
          this.forceClose()
        } else {
          this.close()
        }
        return
      }

      await warn({
        message: 'Warning',
        description:
          'You have unsaved changes in the form, do you want to continue editing?',
        actionRequired: true,
        actions: [
          {
            label: 'Continue editing',
            buttonVariant: 'primary',
            onClick: remove => {
              remove && remove()
            },
          },
          {
            label: 'Discard form changes',
            buttonVariant: 'secondary',
            onClick: remove => {
              remove && remove()
              if (object.isNew) {
                setTimeout(() => {
                  object.remove()
                }, OVERLAY_DURATION)
              } else if (object.isChanged) {
                setTimeout(() => {
                  object.cancel()
                }, OVERLAY_DURATION)
              }
              if (force) {
                this.forceClose()
              } else {
                this.close()
              }
            },
          },
        ],
      })
    }
    list.value.set(key, modalInstance)
    removeOnClose && modalInstance.addEventListener(ModalEvent.CLOSE, () => {
      remove(key)
    })

    return modalInstance
  }

  const remove = (key: string) => {
    list.value.delete(key)
  }

  const closeModals = (entityClass: any, condition?: (entity: any) => boolean) => {
    Array.from(list.value.values()).forEach(modal => {
      if (!(modal.entity instanceof entityClass)) return
      if (condition && !condition(modal.entity)) return
      modal.close()
    })
  }

  // GETTERS

  const getList = computed(() => list.value)

  const getOpened = computed(() => {
    return new Map(Array.from(list.value.entries()).filter(([, modal]) => ![ModalState.CLOSED, ModalState.HIDDEN].includes(modal.state)))
  })

  const getTray = computed(() => {
    return new Map(Array.from(list.value.entries()).filter(([, modal]) => modal.state !== ModalState.CLOSED && modal.allowTray))
  })

  const getFrontModalKey = computed(() => {
    return orderBy(Array.from(getOpened.value.entries()), ([, modal]) => modal.zIndex, ['desc'])?.[0]?.[0]
  })

  const getZIndex = () => {
    return zIndex.value++
  }

  // CLEAR STORE

  const clear = (ignoreModals = ['repositories']) => {
    if (ignoreModals.length) {
      list.value = new Map(
        Array.from(list.value.entries()).filter(([key]) =>
          ignoreModals.includes(key),
        ),
      )
    } else {
      list.value = new Map()
    }
    zIndex.value = MODALS_START_ZINDEX
  }

  const hideLatest = () => {
    const orderedList = orderBy(
      Array.from(list.value.values()),
      ['zIndex'],
      ['desc'],
    )
    const latest = orderedList.find(modal => modal.isOpened)
    latest?.conditionalClose()
  }

  return {
    init,
    remove,
    closeModals,

    getList,
    getOpened,
    getTray,
    getZIndex,
    getFrontModalKey,

    clear,
    hideLatest,
  }
})
